'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { ClaimExtractionModal } from './ClaimExtractionModal';
import { PolicyDetailsPanel } from './PolicyDetailsPanel';
import { DocumentsWorkbench } from '../documents/DocumentsWorkbench';
import { DocumentsSidebar } from '../documents/DocumentsSidebar';
import { DOCUMENT_GROUPS } from '../documents/documentGroups';
import { useDocumentExtractions } from '../documents/hooks/useDocumentExtractions';
import { useImageZoom } from '../documents/hooks/useImageZoom';
import { portalRequestContext, type RequestContext } from '../media/requestContext';
import { getUniqueDamagedParts } from '../media/AiSummaryPanel';
import { listClaimPhotos } from '@/lib/vehicle-claims/api';
import { getReminderLogs, type ReminderLogEntry } from '@/lib/vehicle-claims/api';
import { ReminderLogDialog } from '../ReminderLogDialog';
import type { DamageResult } from '../media/types';

// Dynamically import PdfViewer so PDF.js only loads client-side
const PdfViewer = dynamic(
  () => import('./PdfViewer').then(m => m.PdfViewer),
  { ssr: false, loading: () => (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      <p className="text-sm text-gray-400">Loading PDF viewer…</p>
    </div>
  )}
);

const API_URL    = process.env.NEXT_PUBLIC_API_URL    || 'http://localhost:8000';
const AI_SVC_URL = process.env.NEXT_PUBLIC_AI_SERVICE_URL || 'http://localhost:8103';
const DOCS_BASE = (jobId: string) =>
  `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DocRecord {
  id: string;
  doc_type: string;
  file_name: string | null;
  file_size: number | null;
  content_type: string | null;
  uploaded_at: string;
  download_url: string;
}

// ── Document catalogue ────────────────────────────────────────────────────────
// DocDef / DOCUMENT_GROUPS moved to ../documents/documentGroups.ts and
// ../documents/types.ts (imported above) — see design Section 6.1.

// ── Extraction-capable doc types ──────────────────────────────────────────────

const EXTRACTION_DOC_TYPES = new Set([
  'repair_invoice_1', 'repair_invoice_2', 'repair_invoice_3',
  'estimate_1', 'estimate_2', 'estimate_3',
]);

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('super_admin_token') || '';
  const tenantId = localStorage.getItem('tenant_id') || '';
  const userType = localStorage.getItem('user_type') || '';
  return {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantId,
    'X-User-Type': userType,
  };
}

// Thrown when the refresh request itself couldn't be completed (network
// error, timeout -- no response from the server at all), as opposed to a
// plain `null` return, which means the server was reached and explicitly
// rejected the refresh token. Only the latter means the session is
// genuinely over. This matters most right when a laptop wakes from
// sleep/lock: that's exactly when the access token is most likely to have
// expired AND when Wi-Fi/VPN is most likely still reconnecting -- without
// this distinction, that blip looked identical to an actually-invalid
// refresh token and forced a logout for no real reason.
class RefreshUnavailableError extends Error {}

async function refreshToken(): Promise<string | null> {
  const refreshTok = localStorage.getItem('refresh_token');
  if (!refreshTok) return null;
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshTok }),
    });
  } catch {
    throw new RefreshUnavailableError('Could not reach the refresh endpoint');
  }
  if (!res.ok) return null;
  const data = await res.json();
  const newToken: string | undefined = data?.access_token;
  if (!newToken) return null;
  localStorage.setItem('access_token', newToken);
  if (localStorage.getItem('user_type') === 'super_admin') {
    localStorage.setItem('super_admin_token', newToken);
  }
  return newToken;
}

async function fetchWithRefresh(url: string, init?: RequestInit): Promise<Response> {
  let res = await fetch(url, { ...init, headers: { ...authHeaders(), ...(init?.headers as Record<string, string> || {}) } });
  if (res.status === 401) {
    let newToken: string | null = null;
    try {
      newToken = await refreshToken();
    } catch (err) {
      if (err instanceof RefreshUnavailableError) {
        // Couldn't even attempt the refresh -- leave the session alone
        // and just surface this one request's failure.
        return res;
      }
      throw err;
    }
    if (newToken) {
      res = await fetch(url, { ...init, headers: { ...authHeaders(), ...(init?.headers as Record<string, string> || {}) } });
    }
    if (res.status === 401) {
      const isSuperAdmin = window.location.pathname.startsWith('/super-admin');
      window.location.href = isSuperAdmin ? '/super-admin/login' : '/login';
    }
  }
  return res;
}

// ── OCR field schemas per document type ──────────────────────────────────────

interface FieldSchema { key: string; label: string; multiline?: boolean }

// claims_form extraction — cross-check of the AI Damage Summary (Photos tab)
// against the extracted Cause of Accident / Accident Details, one entry per
// part in the summary. See document_quality.py's damage_consistency_analysis.
interface DamageConsistencyEntry {
  part_name: string;
  estimated_rate: number | null;
  consistency_status: 'Consistent' | 'Inconsistent' | 'Needs Review';
  reason: string;
}

const DOC_FIELD_SCHEMAS: Record<string, FieldSchema[]> = {
  aadhaar_card: [
    { key: 'aadhaar_number', label: 'Aadhaar Number' },
    { key: 'name',           label: 'Name' },
    { key: 'dob',            label: 'Date of Birth' },
    { key: 'gender',         label: 'Gender' },
    { key: 'address',        label: 'Address', multiline: true },
  ],
  pan_card: [
    { key: 'pan_number',   label: 'PAN Number' },
    { key: 'name',         label: 'Name' },
    { key: 'father_name',  label: "Father's Name" },
    { key: 'dob',          label: 'Date of Birth' },
  ],
  // dl_front, dl_back → Driver Details panel (no generic fields)
  // rc_front, rc_back, permit, fitness_fc → Vehicle Details panel (no generic fields)
  policy_copy: [
    { key: 'policy_number', label: 'Policy Number' },
    { key: 'insured_name',  label: 'Insured Name' },
    { key: 'vehicle_reg',   label: 'Vehicle Registration' },
    { key: 'insurer',       label: 'Insurance Company' },
    { key: 'insurer_address', label: 'Insurance Company Address', multiline: true },
    { key: 'insurer_gst_number', label: 'Insurer GST Number' },
    { key: 'valid_from',    label: 'Policy From' },
    { key: 'valid_until',   label: 'Policy Until' },
    { key: 'policy_type',   label: 'Policy Type' },
    { key: 'coverage_type', label: 'Policy Coverage' },
    { key: 'sum_insured',   label: 'Sum Insured (₹)' },
    { key: 'premium',       label: 'Premium (₹)' },
    { key: 'excess_amount', label: 'Excess / Deductible (₹)' },
    { key: 'is_nil_depreciation', label: 'Nil Depreciation Cover' },
    { key: 'add_on_covers', label: 'Add-on Covers', multiline: true },
    { key: 'vehicle_make',  label: 'Vehicle Make' },
    { key: 'vehicle_model', label: 'Vehicle Model' },
    { key: 'vehicle_body_type', label: 'Type of Body' },
    { key: 'engine_number', label: 'Engine Number' },
    { key: 'chassis_number', label: 'Chassis Number' },
  ],
  gc_policy: [
    { key: 'policy_number', label: 'Policy Number' },
    { key: 'insured_name',  label: 'Insured Name' },
    { key: 'vehicle_reg',   label: 'Vehicle Registration' },
    { key: 'insurer',       label: 'Insurance Company' },
    { key: 'insurer_address', label: 'Insurance Company Address', multiline: true },
    { key: 'insurer_gst_number', label: 'Insurer GST Number' },
    { key: 'valid_from',    label: 'Policy From' },
    { key: 'valid_until',   label: 'Policy Until' },
    { key: 'policy_type',   label: 'Policy Type' },
    { key: 'coverage_type', label: 'Policy Coverage' },
    { key: 'sum_insured',   label: 'Sum Insured (₹)' },
    { key: 'premium',       label: 'Premium (₹)' },
    { key: 'excess_amount', label: 'Excess / Deductible (₹)' },
    { key: 'is_nil_depreciation', label: 'Nil Depreciation Cover' },
    { key: 'add_on_covers', label: 'Add-on Covers', multiline: true },
    { key: 'vehicle_make',  label: 'Vehicle Make' },
    { key: 'vehicle_model', label: 'Vehicle Model' },
    { key: 'vehicle_body_type', label: 'Type of Body' },
    { key: 'engine_number', label: 'Engine Number' },
    { key: 'chassis_number', label: 'Chassis Number' },
  ],
  claims_form: [
    { key: 'claim_number',        label: 'Claim Number' },
    { key: 'policy_number',       label: 'Policy Number' },
    { key: 'insurer_name',        label: 'Insurance Company' },
    { key: 'insured_name',        label: 'Insured Name' },
    { key: 'insured_address',     label: 'Insured Address', multiline: true },
    { key: 'insured_city',        label: 'City' },
    { key: 'insured_state',       label: 'State' },
    { key: 'insured_pin',         label: 'PIN Code' },
    { key: 'insured_phone',       label: 'Phone / Mobile' },
    { key: 'insured_email',       label: 'Email' },
    { key: 'vehicle_reg',         label: 'Vehicle Registration' },
    { key: 'vehicle_make',        label: 'Make' },
    { key: 'vehicle_model',       label: 'Model' },
    { key: 'year_of_manufacture', label: 'Year of Manufacture' },
    { key: 'engine_number',       label: 'Engine Number' },
    { key: 'chassis_number',      label: 'Chassis Number' },
    { key: 'incident_date',       label: 'Date of Incident' },
    { key: 'incident_time',       label: 'Time of Incident' },
    { key: 'place_of_accident',   label: 'Place of Accident' },
    { key: 'accident_details',    label: 'Accident Details / Cause (English)', multiline: true },
    { key: 'nature_of_loss',      label: 'Nature of Loss (English)', multiline: true },
    { key: 'damage_description',  label: 'Damage Description (English)', multiline: true },
    { key: 'driver_name',         label: 'Driver Name' },
    { key: 'driver_license_no',   label: 'Driver License No.' },
    { key: 'driver_license_valid', label: 'License Valid Until' },
    { key: 'workshop_name',       label: 'Workshop / Garage Name' },
    { key: 'workshop_address',    label: 'Workshop Address', multiline: true },
    { key: 'bank_account_number', label: 'Bank Account Number' },
    { key: 'bank_ifsc',           label: 'IFSC Code' },
    { key: 'bank_name',           label: 'Bank Name' },
  ],
  cancelled_cheque: [
    { key: 'account_number', label: 'Account Number' },
    { key: 'ifsc_code',      label: 'IFSC Code' },
    { key: 'bank_name',      label: 'Bank Name' },
    { key: 'name',           label: 'Account Holder' },
  ],
};

// ── Vehicle doc constants ─────────────────────────────────────────────────────

const VEHICLE_DOC_TYPES = new Set(['rc_front', 'rc_back', 'permit', 'fitness_fc']);

// Doc types DocOCRModal (the generic side-by-side viewer, used for every
// non-invoice/estimate doc) offers the OCR Extraction UI for. Everything
// else opened in DocOCRModal is view/upload-only — mirrors
// EXTRACTION_DOC_TYPES in DocumentRow.tsx and hooks/useDocumentExtractions.ts
// minus the invoice/estimate types (those never reach DocOCRModal).
const DOC_OCR_MODAL_ELIGIBLE = new Set(['dl_front', 'dl_back', 'rc_front', 'rc_back', 'policy_copy', 'claims_form']);

// Which fields the Vehicle Details panel shows per document type
const VEHICLE_DOC_SECTIONS: Record<string, { title: string; fields: { key: string; label: string; type?: 'select'; options?: string[] }[] }[]> = {
  rc_front: [
    {
      title: 'Vehicle Details',
      fields: [
        { key: 'make',                    label: 'Make' },
        { key: 'model',                   label: 'Model' },
        { key: 'variant',                 label: 'Variant' },
        { key: 'engine_no',               label: 'Engine No' },
        { key: 'vin_no',                  label: 'VIN No' },
        { key: 'seating_capacity',        label: 'Vehicle Seating Capacity' },
        { key: 'type_of_body',            label: 'Type of Body' },
        { key: 'cubic_capacity_kw',       label: 'Cubic Capacity / KW' },
        { key: 'registration_month_year', label: 'Registration Month/Year (MM/YYYY)' },
        { key: 'speedometer_reading',     label: 'Speedometer Reading' },
        { key: 'rc_status',               label: 'RC Status (Manual)', type: 'select', options: ['Active', 'Inactive'] },
      ],
    },
    {
      title: 'RC Information',
      fields: [
        { key: 'reg_number',   label: 'Registration Number' },
        { key: 'owner_name',   label: 'Owner Name' },
        { key: 'fuel_type',    label: 'Fuel Type' },
        { key: 'laden_weight', label: 'Regd. Laden Weight' },
        { key: 'unladen_weight', label: 'Unladen Weight' },
        { key: 'tax_type',     label: 'Tax Type' },
        { key: 'tax_valid_upto', label: 'Tax Valid Upto' },
      ],
    },
  ],
  rc_back: [
    {
      title: 'Vehicle Specifications',
      fields: [
        { key: 'vehicle_class',           label: 'Vehicle Class' },
        { key: 'colour',                  label: 'Colour' },
        { key: 'month_year_of_mfg',       label: 'Month-Year of Manufacture' },
        { key: 'number_of_cylinders',     label: 'Number of Cylinders' },
        { key: 'number_of_axle',          label: 'Number of Axle' },
        { key: 'gross_combination_weight', label: 'Gross Combination Weight (kg)' },
        { key: 'horse_power_bhp_kw',      label: 'Horse Power (BHP/KW)' },
        { key: 'wheel_base_mm',           label: 'Wheel Base (mm)' },
        { key: 'financer_name',           label: 'Financer Name' },
        { key: 'registration_authority',  label: 'Registration Authority' },
      ],
    },
    {
      title: 'Insurance & Validity',
      fields: [
        { key: 'insurance_company',        label: 'Insurance Company' },
        { key: 'insurance_valid_until',    label: 'Insurance Valid Until' },
        { key: 'fitness_certificate_upto', label: 'Fitness Certificate Upto' },
        { key: 'permit_upto',              label: 'Permit Valid Until' },
      ],
    },
  ],
  permit: [
    {
      title: 'Permit Details',
      fields: [
        { key: 'permit_no',            label: 'Permit No' },
        { key: 'permit_upto',          label: 'Permit Upto' },
        { key: 'route_area_operation', label: 'Route / Area Operation' },
      ],
    },
  ],
  fitness_fc: [
    {
      title: 'Fitness Certificate',
      fields: [
        { key: 'fc_certificate_no',    label: 'Certificate No' },
        { key: 'fitness_certificate_upto', label: 'Fitness Valid Upto' },
        { key: 'fc_fitness_issue_date', label: 'Fitness Issue Date' },
        { key: 'fc_inspection_date',   label: 'Inspection Date' },
        { key: 'fc_fitness_status',    label: 'Fitness Status' },
        { key: 'fc_emission_norm',     label: 'Emission Norm' },
        { key: 'fc_pucc_valid_upto',   label: 'PUCC Valid Upto' },
        { key: 'fc_issuing_authority', label: 'Issuing Authority' },
      ],
    },
    {
      title: 'Vehicle Info (from FC)',
      fields: [
        { key: 'reg_number',          label: 'Registration No' },
        { key: 'owner_name',          label: 'Owner Name' },
        { key: 'vin_no',              label: 'Chassis No' },
        { key: 'engine_no',           label: 'Engine No' },
        { key: 'make',                label: 'Vehicle Make' },
        { key: 'model',               label: 'Vehicle Model' },
        { key: 'vehicle_class',       label: 'Vehicle Class' },
        { key: 'fuel_type',           label: 'Fuel Type' },
        { key: 'seating_capacity',    label: 'Seating Capacity' },
        { key: 'gross_vehicle_weight', label: 'Gross Vehicle Weight' },
      ],
    },
  ],
};

// Which form fields to send on save for each doc type (prevents clobbering other docs' fields)
const VEHICLE_SAVE_FIELDS: Record<string, string[]> = {
  rc_front: [
    'make', 'model', 'variant', 'engine_no', 'vin_no', 'seating_capacity',
    'type_of_body', 'cubic_capacity_kw', 'registration_month_year', 'speedometer_reading',
    'rc_status',
    'reg_number', 'owner_name', 'fuel_type', 'laden_weight', 'unladen_weight',
    'tax_type', 'tax_valid_upto',
  ],
  rc_back: [
    'vehicle_class', 'colour', 'month_year_of_mfg', 'number_of_cylinders', 'number_of_axle',
    'gross_combination_weight', 'horse_power_bhp_kw', 'wheel_base_mm', 'financer_name',
    'registration_authority', 'insurance_company', 'insurance_valid_until',
    'fitness_certificate_upto', 'permit_upto',
  ],
  permit:  ['permit_no', 'permit_upto', 'route_area_operation'],
  fitness_fc: [
    'fc_certificate_no', 'fitness_certificate_upto', 'fc_fitness_issue_date',
    'fc_inspection_date', 'fc_fitness_status', 'fc_emission_norm', 'fc_pucc_valid_upto',
    'fc_issuing_authority', 'gross_vehicle_weight',
    'reg_number', 'owner_name', 'vin_no', 'engine_no', 'make', 'model',
    'vehicle_class', 'fuel_type', 'seating_capacity',
  ],
};

// Extraction field → vehicleForm field mapping per doc type
const VEHICLE_EXTRACTION_MAP: Record<string, Record<string, string>> = {
  rc_front: {
    reg_number:       'reg_number',
    owner_name:       'owner_name',
    reg_date:         'registration_month_year',
    engine_number:    'engine_no',
    chassis_number:   'vin_no',
    make:             'make',
    model:            'model',
    variant:          'variant',
    fuel_type:        'fuel_type',
    seating_capacity: 'seating_capacity',
    type_of_body:     'type_of_body',
    cubic_capacity_kw:'cubic_capacity_kw',
    laden_weight:     'laden_weight',
    unladen_weight:   'unladen_weight',
    tax_type:         'tax_type',
    tax_valid_upto:   'tax_valid_upto',
  },
  rc_back: {
    vehicle_class:            'vehicle_class',
    colour:                   'colour',
    month_year_of_mfg:        'month_year_of_mfg',
    number_of_cylinders:      'number_of_cylinders',
    number_of_axle:           'number_of_axle',
    gross_combination_weight: 'gross_combination_weight',
    horse_power_bhp_kw:       'horse_power_bhp_kw',
    wheel_base_mm:            'wheel_base_mm',
    financer_name:            'financer_name',
    registration_authority:   'registration_authority',
    insurance_company:        'insurance_company',
    insurance_valid_until:    'insurance_valid_until',
    fitness_valid_until:      'fitness_certificate_upto',
    permit_valid_until:       'permit_upto',
  },
  permit: {
    permit_number:       'permit_no',
    valid_until:         'permit_upto',
    route_area_operation:'route_area_operation',
  },
  fitness_fc: {
    certificate_no:      'fc_certificate_no',
    reg_number:          'reg_number',
    owner_name:          'owner_name',
    chassis_number:      'vin_no',
    engine_number:       'engine_no',
    make:                'make',
    model:               'model',
    vehicle_class:       'vehicle_class',
    fuel_type:           'fuel_type',
    seating_capacity:    'seating_capacity',
    fitness_status:      'fc_fitness_status',
    fitness_issue_date:  'fc_fitness_issue_date',
    valid_until:         'fitness_certificate_upto',
    inspection_date:     'fc_inspection_date',
    issuing_authority:   'fc_issuing_authority',
    gross_vehicle_weight:'gross_vehicle_weight',
    emission_norm:       'fc_emission_norm',
    pucc_valid_upto:     'fc_pucc_valid_upto',
  },
};

// ── DocOCRModal — side-by-side document viewer + Gemini AI extraction ─────────

export function DocOCRModal({ doc, docLabel, jobId, onClose }: {
  doc: DocRecord;
  docLabel: string;
  jobId: string;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl]     = useState<string | null>(null);
  const [pdfBytes, setPdfBytes]   = useState<Uint8Array | null>(null);
  const [rawBuffer, setRawBuffer] = useState<ArrayBuffer | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [docErr, setDocErr]       = useState<string | null>(null);
  const [rotation, setRotation]   = useState(0);
  const imageZoom = useImageZoom(doc.id);

  // Garage/Claim Office users can view any document but the OCR extraction
  // UI itself (Extract button, extracted-fields panel, driver/vehicle/
  // policy save panels) is hidden - enforced again server-side
  // (claim_documents.py _ALLOWED_STAFF_ONLY), this is UX only.
  const isGarageViewer =
    typeof window !== 'undefined' &&
    ['GARAGE', 'CLAIM_OFFICE'].includes(localStorage.getItem('user_type')?.toUpperCase() ?? '');
  // Documents tab restricts the OCR Extraction UI to Repair Invoice, DL
  // Front/Back, RC Front/Back, Policy, and Claims Form. Every other doc type
  // that lands in this generic viewer (Aadhaar, PAN, GC Policy, Permit,
  // Fitness/FC, Estimates, payment proofs, etc.) is view/upload-only here.
  const ocrEnabled = DOC_OCR_MODAL_ELIGIBLE.has(doc.doc_type);
  const hideOcrUI = isGarageViewer || !ocrEnabled;
  const [extracting, setExtracting]         = useState(false);
  const [extractErr, setExtractErr]         = useState<string | null>(null);
  const [extractedFields, setExtractedFields] = useState<Record<string, unknown>>({});
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const [confidenceLabel, setConfidenceLbl]   = useState<string | null>(null);
  const [provider, setProvider]             = useState<string | null>(null);
  const [hasExtracted, setHasExtracted]     = useState(false);
  const [damageConsistency, setDamageConsistency] = useState<DamageConsistencyEntry[]>([]);

  // Editable fields state (user can modify extracted values)
  const [editedFields, setEditedFields] = useState<Record<string, string>>({});

  // Save state (generic extraction)
  const [saving, setSaving]     = useState(false);
  const [saveErr, setSaveErr]   = useState<string | null>(null);
  const [savedOk, setSavedOk]   = useState(false);

  // Verified checkbox — aadhaar_card, pan_card, cancelled_cheque, claims_form
  const [verified, setVerified] = useState(false);

  // DL-specific driver detail state
  const isDL = doc.doc_type === 'dl_front' || doc.doc_type === 'dl_back';
  const [driverForm, setDriverForm] = useState<Record<string, string>>({});

  // Policy Document state (policy_copy, gc_policy) — structured save to
  // claim_policy_details, replacing the generic flat-JSON save path for
  // these two doc types (see PolicyDetailsPanel)
  const isPolicyDoc = doc.doc_type === 'policy_copy' || doc.doc_type === 'gc_policy';
  const [policySaved, setPolicySaved] = useState<Record<string, unknown> | null>(null);

  // Vehicle-specific state (rc_front, rc_back, permit, fitness_fc)
  const isVehicleDoc = VEHICLE_DOC_TYPES.has(doc.doc_type);
  const [vehicleForm, setVehicleForm] = useState<Record<string, string>>({});
  const [vehicleVerified, setVehicleVerified] = useState(false);
  const [vehicleSaving, setVehicleSaving]     = useState(false);
  const [vehicleSaveErr, setVehicleSaveErr]   = useState<string | null>(null);
  const [vehicleSavedOk, setVehicleSavedOk]   = useState(false);
  const [driverVerified, setDriverVerified] = useState(false);
  const [driverSaving, setDriverSaving]     = useState(false);
  const [driverSaveErr, setDriverSaveErr]   = useState<string | null>(null);
  const [driverSavedOk, setDriverSavedOk]   = useState(false);

  const isPdf  = doc.content_type === 'application/pdf' || /\.pdf$/i.test(doc.file_name || '');
  const isImg  = !isPdf;

  // Load document bytes
  useEffect(() => {
    let cancelled = false;
    setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPdfBytes(null);
    setRawBuffer(null);
    setLoadingDoc(true);
    setDocErr(null);

    fetch(doc.download_url)
      .then(r => { if (!r.ok) throw new Error(`Fetch failed (${r.status})`); return r.arrayBuffer(); })
      .then(buf => {
        if (cancelled) return;
        setRawBuffer(buf.slice(0));
        if (isPdf) {
          setPdfBytes(new Uint8Array(buf));
        } else {
          setBlobUrl(URL.createObjectURL(new Blob([buf], { type: doc.content_type || 'image/jpeg' })));
        }
      })
      .catch(e => { if (!cancelled) setDocErr(e instanceof Error ? e.message : 'Load failed'); })
      .finally(() => { if (!cancelled) setLoadingDoc(false); });

    return () => { cancelled = true; };
  }, [doc.id, isPdf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup blob URL on unmount
  useEffect(() => () => { setBlobUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; }); }, []);

  // Load existing saved extraction on open
  useEffect(() => {
    let cancelled = false;
    const url = `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${doc.id}/extraction`;
    fetchWithRefresh(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data?.extracted_data) return;
        const raw = { ...(data.extracted_data as Record<string, unknown>) };
        const consistency = Array.isArray(raw.damage_consistency_analysis)
          ? raw.damage_consistency_analysis as DamageConsistencyEntry[]
          : [];
        delete raw.damage_consistency_analysis;
        setDamageConsistency(consistency);
        setExtractedFields(raw);
        const flat: Record<string, string> = {};
        Object.entries(raw).forEach(([k, v]) => {
          if (v != null && v !== 'null' && v !== '') flat[k] = String(v);
        });
        setEditedFields(flat);
        setHasExtracted(true);
        setVerified(Boolean(data.is_verified));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.id, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For DL docs: load existing driver details on open
  useEffect(() => {
    if (!isDL) return;
    let cancelled = false;
    const url = `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/driver-details`;
    fetchWithRefresh(url)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const form: Record<string, string> = {};
        const keys = ['driver_name','driving_licence_no','date_of_issue','valid_upto',
                      'issuing_authority','type_of_licence','badge_no','address',
                      'father_name','date_of_birth','gender'];
        keys.forEach(k => { if (data[k]) form[k] = String(data[k]); });
        if (Object.keys(form).length > 0) {
          setDriverForm(form);
          setDriverVerified(data.is_verified ?? false);
          // Mark as "has data" so the Driver Details panel renders on reload
          setHasExtracted(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.id, jobId, isDL]); // eslint-disable-line react-hooks/exhaustive-deps

  // For Policy docs: load existing policy details on open
  useEffect(() => {
    if (!isPolicyDoc) return;
    let cancelled = false;
    fetchWithRefresh(`${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/policy-details`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        setPolicySaved(data);
        setHasExtracted(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.id, jobId, isPolicyDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard close
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function extract() {
    if (!rawBuffer) return;
    setExtracting(true);
    setExtractErr(null);
    setSavedOk(false);
    try {
      const fd = new FormData();
      fd.append('file', new Blob([rawBuffer.slice(0)], { type: doc.content_type || 'image/jpeg' }), doc.file_name || 'document');
      fd.append('doc_type', doc.doc_type);

      // claims_form: fetch the Photos tab's AI Damage Summary (unique damaged
      // parts + estimated rates) and pass it as extra context so the LLM can
      // cross-check the extracted Cause of Accident / Accident Details
      // against each damaged part. Best-effort — extraction proceeds with
      // its existing behaviour if this fetch fails or finds nothing.
      if (doc.doc_type === 'claims_form') {
        try {
          const photos = await listClaimPhotos(jobId);
          const assessed = photos
            .filter(p => !!p.detected_damage)
            .map(p => p.detected_damage as DamageResult);
          const uniqueParts = getUniqueDamagedParts(assessed);
          if (uniqueParts.length > 0) {
            fd.append('damaged_parts', JSON.stringify(uniqueParts.map(part => ({
              part_name: part.part_name,
              estimated_rate: part.estimated_cost?.recommended_cost ?? null,
            }))));
          }
        } catch {
          // Non-fatal — Claim Form extraction still runs without the summary.
        }
      }

      const res = await fetchWithRefresh(`${AI_SVC_URL}/api/v1/ai/document/extract-fields`, {
        method: 'POST',
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { detail?: string }).detail || `Extraction failed (${res.status})`);
      }
      const data = await res.json();
      const fields = { ...(data.fields || {}) };
      const consistency = Array.isArray(fields.damage_consistency_analysis)
        ? fields.damage_consistency_analysis as DamageConsistencyEntry[]
        : [];
      delete fields.damage_consistency_analysis;
      setDamageConsistency(consistency);
      setExtractedFields(fields);
      // Initialise editable fields from the extraction result
      const flat: Record<string, string> = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (v != null && v !== 'null' && v !== '') flat[k] = String(v);
      });
      setEditedFields(flat);
      setConfidenceScore(data.confidence_score ?? null);
      setConfidenceLbl(data.confidence ?? null);
      setProvider(data.provider ?? null);
      setHasExtracted(true);

      // claims_form: once the AI Parts Consistency Analysis comes back
      // successfully, persist it alongside the extracted Claim Form data
      // immediately — the Work Order tab's summary reads it from
      // extracted_data and must not depend on the user clicking Save.
      if (doc.doc_type === 'claims_form' && consistency.length > 0) {
        try {
          const patchUrl = `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${doc.id}/extraction`;
          await fetchWithRefresh(patchUrl, {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...flat, damage_consistency_analysis: consistency }),
          });
        } catch {
          // Non-fatal — the on-screen summary still shows this session's
          // result; the Work Order tab just won't see it until re-saved.
        }
      }
    } catch (e) {
      setExtractErr(e instanceof Error ? e.message : 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  }

  // For vehicle docs: load existing vehicle details on open
  useEffect(() => {
    if (!isVehicleDoc) return;
    let cancelled = false;
    fetchWithRefresh(`${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/vehicle-details`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const form: Record<string, string> = {};
        const keys = [
          'make','model','variant','engine_no','vin_no','seating_capacity',
          'type_of_body','cubic_capacity_kw','registration_month_year','speedometer_reading',
          'rc_status',
          'reg_number','owner_name','fuel_type','laden_weight','unladen_weight',
          'tax_type','tax_valid_upto',
          'vehicle_class','colour','month_year_of_mfg','number_of_cylinders','number_of_axle',
          'gross_combination_weight','horse_power_bhp_kw','wheel_base_mm',
          'financer_name','registration_authority',
          'insurance_company','insurance_valid_until',
          'fitness_certificate_upto','permit_no','permit_upto','route_area_operation',
          'fc_certificate_no','fc_fitness_status','fc_fitness_issue_date','fc_inspection_date',
          'fc_emission_norm','fc_pucc_valid_upto','fc_issuing_authority','gross_vehicle_weight',
        ];
        keys.forEach(k => { if (data[k]) form[k] = String(data[k]); });
        if (Object.keys(form).length > 0) {
          setVehicleForm(form);
          setVehicleVerified(data.is_verified ?? false);
          setHasExtracted(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc.id, jobId, isVehicleDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  // When extraction completes on a vehicle doc, auto-populate vehicleForm
  useEffect(() => {
    if (!isVehicleDoc || Object.keys(extractedFields).length === 0) return;
    const fieldMap = VEHICLE_EXTRACTION_MAP[doc.doc_type] ?? {};
    setVehicleForm(prev => {
      const next = { ...prev };
      Object.entries(fieldMap).forEach(([src, dst]) => {
        const v = (extractedFields as Record<string, unknown>)[src];
        if (v != null && v !== 'null' && v !== '') next[dst] = String(v);
      });
      return next;
    });
  }, [extractedFields, isVehicleDoc]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveVehicleDetails() {
    setVehicleSaving(true);
    setVehicleSaveErr(null);
    setVehicleSavedOk(false);
    try {
      const docIdField: Record<string, string> = {};
      if (doc.doc_type === 'rc_front')   docIdField.rc_front_doc_id = doc.id;
      if (doc.doc_type === 'rc_back')    docIdField.rc_back_doc_id  = doc.id;
      if (doc.doc_type === 'permit')     docIdField.permit_doc_id   = doc.id;
      if (doc.doc_type === 'fitness_fc') docIdField.fc_doc_id       = doc.id;

      // Only send fields relevant to this doc type to avoid nulling other docs' data
      const saveFields = VEHICLE_SAVE_FIELDS[doc.doc_type] ?? [];
      const body: Record<string, string | boolean | null> = { ...docIdField, is_verified: vehicleVerified };
      saveFields.forEach(f => { body[f] = vehicleForm[f] || null; });

      const res = await fetchWithRefresh(
        `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/vehicle-details`,
        {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || `Save failed (${res.status})`);
      }
      setVehicleSavedOk(true);
      setTimeout(() => setVehicleSavedOk(false), 3000);
    } catch (e) {
      setVehicleSaveErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setVehicleSaving(false);
    }
  }

  // When extraction completes on a DL doc, auto-populate driverForm from the result.
  // Depends on extractedFields (not hasExtracted) so it re-runs even when hasExtracted
  // was already true from a prior DB load.
  useEffect(() => {
    if (!isDL || Object.keys(extractedFields).length === 0) return;
    setDriverForm(prev => {
      const next = { ...prev };
      const map: Record<string, string> = {
        name:             'driver_name',
        document_number:  'driving_licence_no',
        issue_date:       'date_of_issue',
        valid_until:      'valid_upto',
        issuing_authority:'issuing_authority',
        vehicle_classes:  'type_of_licence',
        badge_number:     'badge_no',
        address:          'address',
        father_name:      'father_name',
        dob:              'date_of_birth',
        gender:           'gender',
      };
      Object.entries(map).forEach(([src, dst]) => {
        const v = (extractedFields as Record<string, unknown>)[src];
        if (v != null && v !== 'null' && v !== '') next[dst] = String(v);
      });
      return next;
    });
  }, [extractedFields, isDL]); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveDriverDetails() {
    setDriverSaving(true);
    setDriverSaveErr(null);
    setDriverSavedOk(false);
    try {
      const docIdField = doc.doc_type === 'dl_front'
        ? { dl_front_doc_id: doc.id }
        : { dl_back_doc_id: doc.id };
      const body = {
        ...driverForm,
        ...docIdField,
        is_verified: driverVerified,
      };
      const res = await fetchWithRefresh(
        `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/driver-details`,
        {
          method: 'PATCH',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || `Save failed (${res.status})`);
      }
      setDriverSavedOk(true);
      setTimeout(() => setDriverSavedOk(false), 3000);
    } catch (e) {
      setDriverSaveErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setDriverSaving(false);
    }
  }

  async function saveExtraction() {
    setSaving(true);
    setSaveErr(null);
    setSavedOk(false);
    try {
      const url = `${API_URL}/api/v1/insurance/vehicle-claims/${jobId}/documents/${doc.id}/extraction`;
      // extracted_data is replaced wholesale by this PATCH — re-include the
      // already-persisted damage_consistency_analysis (if any) so editing a
      // field here doesn't drop the Work Order tab's summary.
      const body: Record<string, unknown> = { ...editedFields, is_verified: verified };
      if (damageConsistency.length > 0) body.damage_consistency_analysis = damageConsistency;
      const res = await fetchWithRefresh(url, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error((errBody as { detail?: string }).detail || `Save failed (${res.status})`);
      }
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const schema = DOC_FIELD_SCHEMAS[doc.doc_type] ?? [];

  function badgeClass(score: number) {
    if (score >= 90) return 'text-green-700 bg-green-50 border-green-200';
    if (score >= 70) return 'text-blue-700 bg-blue-50 border-blue-200';
    if (score >= 50) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-red-700 bg-red-50 border-red-200';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/75 p-0 sm:items-center sm:p-3 sm:backdrop-blur-sm">
    <div className="relative flex w-full flex-col overflow-hidden bg-white sm:rounded-xl sm:shadow-2xl" style={{ maxWidth: '1400px', height: '100vh', maxHeight: '100vh' }}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-white px-5 py-3 flex-shrink-0">
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900 truncate">{docLabel}</p>
          {doc.file_name && <p className="text-xs text-gray-400 truncate">{doc.file_name}</p>}
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {!hideOcrUI && (
            <>
              <button
                onClick={extract}
                disabled={extracting || loadingDoc || !rawBuffer}
                className="flex items-center gap-1.5 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {extracting ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-white flex-shrink-0" />
                    Extracting…
                  </>
                ) : (
                  <>
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Extract (OCR)
                  </>
                )}
              </button>
              {hasExtracted && !isDL && !isVehicleDoc && !isPolicyDoc && (
                <button
                  onClick={saveExtraction}
                  disabled={saving}
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50 transition-colors ${savedOk ? 'bg-green-600 hover:bg-green-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                >
                  {saving ? (
                    <>
                      <div className="h-3.5 w-3.5 animate-spin rounded-full border-b-2 border-white flex-shrink-0" />
                      Saving…
                    </>
                  ) : savedOk ? (
                    <>
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Saved
                    </>
                  ) : (
                    <>
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save
                    </>
                  )}
                </button>
              )}
            </>
          )}
          <a
            href={doc.download_url}
            download={doc.file_name || true}
            className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download
          </a>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body: LEFT = document viewer, RIGHT = OCR panel */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* LEFT: Document viewer */}
        <div className={hideOcrUI ? 'w-full flex-shrink-0 bg-gray-50 flex flex-col' : 'w-1/2 flex-shrink-0 bg-gray-50 flex flex-col border-r border-gray-100'}>
          {/* Rotate toolbar for images */}
          {!loadingDoc && !docErr && isImg && blobUrl && (
            <div className="flex items-center gap-2 border-b border-gray-100 bg-white px-4 py-1.5 flex-shrink-0">
              <span className="text-xs text-gray-400 mr-1">Rotate</span>
              <button
                onClick={() => setRotation(r => (r - 90 + 360) % 360)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="Rotate left"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <button
                onClick={() => setRotation(r => (r + 90) % 360)}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                title="Rotate right"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              {rotation !== 0 && (
                <button onClick={() => setRotation(0)} className="text-xs text-gray-400 hover:text-gray-600">
                  Reset
                </button>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={imageZoom.zoomOut}
                  disabled={imageZoom.zoom <= 0.5}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  title="Zoom out"
                >
                  −
                </button>
                <span className="w-9 text-center text-xs tabular-nums text-gray-400">{Math.round(imageZoom.zoom * 100)}%</span>
                <button
                  onClick={imageZoom.zoomIn}
                  disabled={imageZoom.zoom >= 5}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                  title="Zoom in"
                >
                  +
                </button>
                {imageZoom.zoom !== 1 && (
                  <button onClick={imageZoom.reset} className="text-xs text-gray-400 hover:text-gray-600">
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto">
            {loadingDoc && (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                <p className="text-sm text-gray-400">Loading document…</p>
              </div>
            )}
            {!loadingDoc && docErr && (
              <div className="flex flex-col items-center justify-center h-full gap-3 p-6">
                <svg className="h-12 w-12 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-500 text-center">{docErr}</p>
                <a href={doc.download_url} target="_blank" rel="noopener noreferrer"
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
                  Open in new tab
                </a>
              </div>
            )}
            {!loadingDoc && !docErr && isImg && blobUrl && (
              <div
                className="flex h-full items-center justify-center overflow-hidden p-6"
                onWheel={imageZoom.onWheel}
                onMouseDown={imageZoom.onMouseDown}
                onMouseMove={imageZoom.onMouseMove}
                onMouseUp={imageZoom.stopDrag}
                onMouseLeave={imageZoom.stopDrag}
                style={{ cursor: imageZoom.zoom > 1 ? (imageZoom.dragging ? 'grabbing' : 'grab') : 'default' }}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={blobUrl}
                  alt={docLabel}
                  draggable={false}
                  style={{
                    transform: `translate(${imageZoom.pan.x}px, ${imageZoom.pan.y}px) scale(${imageZoom.zoom}) rotate(${rotation}deg)`,
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    transition: imageZoom.dragging ? 'none' : 'transform 0.15s ease',
                  }}
                  className="rounded shadow-sm select-none"
                />
              </div>
            )}
            {!loadingDoc && !docErr && isPdf && pdfBytes && (
              <div className="h-full">
                <PdfViewer pdfBytes={pdfBytes} maxWidth={700} />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: OCR extraction panel - hidden for garage viewers (server
            already blocks the underlying extraction/driver/vehicle/policy
            endpoints for them) and for doc types outside the Documents tab's
            OCR-eligible list (DOC_OCR_MODAL_ELIGIBLE) - CSS-hidden here too
            so nothing flashes. */}
        <div className={hideOcrUI ? 'hidden' : 'w-1/2 flex flex-col overflow-y-auto'}>
          <div className="px-6 py-5 space-y-4">

            {/* Confidence badge */}
            {hasExtracted && confidenceScore !== null && (
              <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${badgeClass(confidenceScore)}`}>
                <div className="text-2xl font-bold">{confidenceScore}</div>
                <div>
                  <p className="text-sm font-semibold capitalize">{confidenceLabel ?? 'Unknown'} confidence</p>
                  {provider && <p className="text-xs opacity-70">Extracted via OCR</p>}
                </div>
              </div>
            )}

            {/* Extraction error */}
            {extractErr && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">Extraction failed</p>
                <p className="text-xs text-red-600 mt-0.5">{extractErr}</p>
                <button onClick={extract} className="mt-2 text-xs text-red-700 underline">
                  Try again
                </button>
              </div>
            )}

            {/* Save error */}
            {saveErr && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">Save failed</p>
                <p className="text-xs text-red-600 mt-0.5">{saveErr}</p>
              </div>
            )}

            {/* Idle state */}
            {!hasExtracted && !extracting && !extractErr && (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                <svg className="h-12 w-12 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <p className="text-sm text-gray-400">
                  Click <strong>Extract (OCR)</strong> to auto-read this document
                </p>
                {isDL && (
                  <p className="text-xs text-blue-500">Driver details will appear below for verification and saving</p>
                )}
                {isVehicleDoc && (
                  <p className="text-xs text-emerald-600">Vehicle details will appear below for verification and saving</p>
                )}
              </div>
            )}

            {/* Extracting spinner */}
            {extracting && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-violet-500" />
                <p className="text-sm text-gray-500">Reading document (OCR)…</p>
              </div>
            )}

            {/* Extracted fields — editable (generic, hidden for DL, vehicle, and policy docs) */}
            {hasExtracted && !extracting && !isDL && !isVehicleDoc && !isPolicyDoc && (
              <div className="space-y-3">
                <p className="text-xs text-gray-400 -mb-1">Fields are editable. Click Save to store changes.</p>
                {schema.length > 0 ? (
                  schema.map(f => {
                    const val = editedFields[f.key] ?? '';
                    return (
                      <div key={f.key}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          {f.label}
                        </label>
                        {f.multiline ? (
                          <textarea
                            value={val}
                            onChange={e => setEditedFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                            rows={3}
                            placeholder="—"
                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400 resize-y"
                          />
                        ) : (
                          <input
                            type="text"
                            value={val}
                            onChange={e => setEditedFields(prev => ({ ...prev, [f.key]: e.target.value }))}
                            placeholder="—"
                            className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  Object.entries(extractedFields)
                    .filter(([k, v]) => v != null && v !== 'null' && v !== '' && k !== 'confidence')
                    .map(([k]) => (
                      <div key={k}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          {k.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="text"
                          value={editedFields[k] ?? ''}
                          onChange={e => setEditedFields(prev => ({ ...prev, [k]: e.target.value }))}
                          placeholder="—"
                          className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-400"
                        />
                      </div>
                    ))
                )}

                {doc.doc_type === 'claims_form' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Insured Signature Present
                    </label>
                    <p className="text-sm text-gray-900">
                      {extractedFields.insured_signature_present === true ? 'Yes' : 'No'}
                    </p>
                  </div>
                )}

                {doc.doc_type === 'claims_form' && damageConsistency.length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Damage Consistency vs. AI Damage Summary
                    </p>
                    <ul className="space-y-2">
                      {damageConsistency.map((entry, i) => (
                        <li key={`${entry.part_name}-${i}`} className="rounded-lg border border-gray-200 bg-white p-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{entry.part_name}</span>
                            <span
                              className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                entry.consistency_status === 'Consistent'
                                  ? 'text-green-700 bg-green-50 border-green-200'
                                  : entry.consistency_status === 'Inconsistent'
                                  ? 'text-red-700 bg-red-50 border-red-200'
                                  : 'text-amber-700 bg-amber-50 border-amber-200'
                              }`}
                            >
                              {entry.consistency_status}
                            </span>
                          </div>
                          {entry.estimated_rate != null && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Estimated rate: ₹{entry.estimated_rate.toLocaleString('en-IN')}
                            </p>
                          )}
                          <p className="text-xs text-gray-600 mt-1">{entry.reason}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {['aadhaar_card', 'pan_card', 'cancelled_cheque', 'claims_form'].includes(doc.doc_type) && (
                  <label className="flex items-start gap-2.5 cursor-pointer select-none pt-1">
                    <input
                      type="checkbox"
                      checked={verified}
                      onChange={e => setVerified(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                    />
                    <span className="text-xs text-gray-700 leading-relaxed">
                      I have verified this document against the extracted data and confirm it is accurate.
                    </span>
                  </label>
                )}
              </div>
            )}

            {/* ── Vehicle Details — verify & save to claim_vehicle_details ── */}
            {isVehicleDoc && hasExtracted && !extracting && (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 space-y-4">
                {/* Header */}
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10l2 2h10l2-2z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-emerald-900">Vehicle Details</h3>
                  <span className="text-xs text-emerald-500 ml-auto">Saved to vehicle record</span>
                </div>

                {/* Field sections specific to this document type */}
                {(VEHICLE_DOC_SECTIONS[doc.doc_type] ?? []).map((section, si) => (
                  <div key={section.title}>
                    {(VEHICLE_DOC_SECTIONS[doc.doc_type]?.length ?? 0) > 1 && (
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{section.title}</p>
                    )}
                    <div className="space-y-3">
                      {section.fields.map(f => (
                        <div key={f.key}>
                          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                            {f.label}
                          </label>
                          {f.type === 'select' ? (
                            <select
                              value={vehicleForm[f.key] ?? ''}
                              onChange={e => setVehicleForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                              className="w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                            >
                              <option value="">— Select —</option>
                              {f.options?.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={vehicleForm[f.key] ?? ''}
                              onChange={e => setVehicleForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                              placeholder="—"
                              className="w-full rounded-md border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    {si < (VEHICLE_DOC_SECTIONS[doc.doc_type]?.length ?? 0) - 1 && (
                      <div className="mt-4 border-t border-emerald-100" />
                    )}
                  </div>
                ))}

                {/* Save error */}
                {vehicleSaveErr && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{vehicleSaveErr}</p>
                )}

                {/* Verification checkbox */}
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={vehicleVerified}
                    onChange={e => setVehicleVerified(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                  />
                  <span className="text-xs text-gray-700 leading-relaxed">
                    I have verified all vehicle information against the physical document and confirm it is accurate.
                  </span>
                </label>

                {/* Save button */}
                <button
                  onClick={saveVehicleDetails}
                  disabled={!vehicleVerified || vehicleSaving}
                  className={`w-full flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    vehicleSavedOk ? 'bg-green-600 hover:bg-green-700' : 'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                >
                  {vehicleSaving ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Saving…
                    </>
                  ) : vehicleSavedOk ? (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Vehicle Details Saved
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save Vehicle Details
                    </>
                  )}
                </button>
                {!vehicleVerified && (
                  <p className="text-center text-xs text-gray-400">Tick the verification checkbox to enable save</p>
                )}
              </div>
            )}

            {/* ── Policy Details — verify & save to claim_policy_details ── */}
            {isPolicyDoc && hasExtracted && !extracting && (
              <PolicyDetailsPanel
                jobId={jobId}
                docId={doc.id}
                extractedFields={extractedFields}
                savedFields={policySaved}
                onSaved={() => {}}
              />
            )}

            {/* ── DL Driver Details — verify & save to claim_driver_details ── */}
            {isDL && hasExtracted && !extracting && (
              <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-4">
                {/* Section header */}
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <h3 className="text-sm font-semibold text-blue-900">Driver Details</h3>
                  <span className="text-xs text-blue-500 ml-auto">Saved to driver record</span>
                </div>

                {/* 8 core driver fields — Date of Birth sits right after Driving
                    Licence No (not buried in Additional Information) since
                    those two are exactly what triggers the Parivahan DL API
                    fetch on save (see save_driver_details in
                    claim_documents.py: `if driving_licence_no and date_of_birth`). */}
                <div className="space-y-3">
                  {([
                    { key: 'driver_name',       label: 'Driver Name' },
                    { key: 'driving_licence_no', label: 'Driving Licence No' },
                    { key: 'date_of_birth',      label: 'Date of Birth' },
                    { key: 'date_of_issue',      label: 'Date of Issue' },
                    { key: 'valid_upto',         label: 'Valid Upto' },
                    { key: 'issuing_authority',  label: 'Issuing Authority' },
                    { key: 'type_of_licence',    label: 'Type of Licence' },
                    { key: 'badge_no',           label: 'Badge No' },
                  ] as const).map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                        {f.label}
                      </label>
                      <input
                        type="text"
                        value={driverForm[f.key] ?? ''}
                        onChange={e => setDriverForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        placeholder="—"
                        className="w-full rounded-md border border-blue-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-300 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>
                  ))}
                </div>

                {/* Additional information (address, father_name, gender) */}
                {(driverForm.address || driverForm.father_name || driverForm.gender) && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Additional Information</p>
                    <div className="rounded-md border border-blue-100 bg-white px-3 py-2 space-y-1">
                      {driverForm.father_name  && <p className="text-xs text-gray-700"><span className="font-medium text-gray-500">Father&apos;s Name:</span> {driverForm.father_name}</p>}
                      {driverForm.gender        && <p className="text-xs text-gray-700"><span className="font-medium text-gray-500">Gender:</span> {driverForm.gender}</p>}
                      {driverForm.address && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-0.5">Address:</p>
                          <textarea
                            value={driverForm.address}
                            onChange={e => setDriverForm(prev => ({ ...prev, address: e.target.value }))}
                            rows={2}
                            className="w-full rounded border border-blue-100 bg-gray-50 px-2 py-1 text-xs text-gray-800 focus:border-blue-300 focus:outline-none resize-y"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Driver save error */}
                {driverSaveErr && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{driverSaveErr}</p>
                )}

                {/* Verification checkbox */}
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={driverVerified}
                    onChange={e => setDriverVerified(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  />
                  <span className="text-xs text-gray-700 leading-relaxed">
                    I have verified all driver information against the physical document and confirm it is accurate.
                  </span>
                </label>

                {/* Save driver details button */}
                <button
                  onClick={saveDriverDetails}
                  disabled={!driverVerified || driverSaving}
                  className={`w-full flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    driverSavedOk
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {driverSaving ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                      Saving…
                    </>
                  ) : driverSavedOk ? (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      Driver Details Saved
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Save Driver Details
                    </>
                  )}
                </button>
                {!driverVerified && (
                  <p className="text-center text-xs text-gray-400">Tick the verification checkbox to enable save</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export function ClaimDocumentsTab({
  jobId,
  mode,
  shareToken,
  claimNumber,
  statusCode,
}: {
  jobId: string;
  mode?: 'staff' | 'portal';
  shareToken?: string;
  claimNumber?: string | null;
  statusCode?: string;
}) {
  const [docs, setDocs]     = useState<DocRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderLogs, setReminderLogs] = useState<ReminderLogEntry[]>([]);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderErr, setReminderErr] = useState<string | null>(null);
  async function openReminderLogs() {
    setReminderOpen(true);
    setReminderLoading(true);
    setReminderErr(null);
    try {
      setReminderLogs(await getReminderLogs(jobId));
    } catch (e) {
      setReminderErr(e instanceof Error ? e.message : 'Could not load the reminder log');
    } finally {
      setReminderLoading(false);
    }
  }

  const requestContext: RequestContext | undefined = useMemo(
    () => (mode === 'portal' && shareToken ? portalRequestContext(shareToken) : undefined),
    [mode, shareToken],
  );

  const loadDocs = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const res = requestContext
        ? await fetch(`${requestContext.baseUrl}/${jobId}/documents`, { headers: requestContext.headers })
        : await fetchWithRefresh(DOCS_BASE(jobId));
      if (!res.ok) throw new Error(`Failed to load documents (${res.status})`);
      setDocs(await res.json());
    } catch (e: unknown) {
      setLoadErr(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [jobId, requestContext]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  // viewerDoc: non-invoice doc open in the side-by-side OCR modal
  const [viewerDoc, setViewerDoc]         = useState<DocRecord | null>(null);
  // extraction modal: doc opened for invoice/estimate data entry
  const [extractionDoc, setExtractionDoc] = useState<DocRecord | null>(null);

  function onUploaded(rec: DocRecord) {
    setDocs(prev => [...prev.filter(d => d.doc_type !== rec.doc_type), rec]);
  }

  function onDeleted(id: string) {
    setDocs(prev => prev.filter(d => d.id !== id));
    setViewerDoc(null);
    setExtractionDoc(null);
  }

  const isGarage =
    typeof window !== 'undefined' &&
    ['GARAGE', 'CLAIM_OFFICE'].includes(localStorage.getItem('user_type')?.toUpperCase() ?? '');

  // Claim Office (unlike Garage) is view-only on the Documents tab — no
  // Upload/Replace/Del, just View/Review. Enforced again server-side
  // (require_claim_write_access), this is UX only.
  const isClaimOfficeViewOnly =
    typeof window !== 'undefined' &&
    (localStorage.getItem('user_type')?.toUpperCase() ?? '') === 'CLAIM_OFFICE';

  // Garage (logged in or via this share-link portal) can Delete only while
  // the claim is still in its initial intake/review phase -- same set as
  // claim_status_engine.py's AUTO_MANAGED_STATUS_CODES. Upload/Replace stay
  // available at any status (readOnly above, not this). Enforced again
  // server-side (require_claim_delete_access), this is UX only.
  const GARAGE_DELETE_ALLOWED_STATUSES = new Set([
    'fresh', 'pending_documents', 'pending_photo_upload', 'ready_for_initial_approval',
  ]);
  const isGarageOnly =
    mode === 'portal' ||
    (typeof window !== 'undefined' && (localStorage.getItem('user_type')?.toUpperCase() ?? '') === 'GARAGE');
  const deleteDisabled =
    isGarageOnly && !(statusCode && GARAGE_DELETE_ALLOWED_STATUSES.has(statusCode));

  function onView(rec: DocRecord) {
    if (mode === 'portal') return;
    // Garage users can view any document but never the OCR extraction UI.
    // ClaimExtractionModal (invoice/estimate) has no plain-view mode of its
    // own, so route those through DocOCRModal too - it hides its own
    // extraction panel for garage viewers, giving a consistent view-only
    // experience across every doc type. Enforced again server-side.
    if (isGarage) {
      setViewerDoc(rec);
      return;
    }
    if (EXTRACTION_DOC_TYPES.has(rec.doc_type)) {
      setExtractionDoc(rec);
    } else {
      setViewerDoc(rec);
    }
  }

  // Flat list of all definitions (for viewer label lookup)
  const allDefs = DOCUMENT_GROUPS.flatMap(g => g.docs.map(d => ({ key: d.key, label: d.label })));

  // Other extraction-capable docs currently uploaded for this claim, paired
  // with their display labels. Feeds ClaimExtractionModal's siblingDocs prop,
  // which powers the segmented Estimate/Invoice switcher (design Section 6.5)
  // and the read-only earlier-invoice-slot preview shown when extracting
  // Repair Invoice 2/3.
  const siblingDocs = docs
    .filter(d => EXTRACTION_DOC_TYPES.has(d.doc_type))
    .map(d => ({ doc: d, docLabel: allDefs.find(def => def.key === d.doc_type)?.label ?? d.doc_type }));

  const uploadedCount = docs.length;
  const totalDocs = DOCUMENT_GROUPS.reduce((sum, g) => sum + g.docs.length, 0);

  // Parallel per-document extraction fetch (confidence/mismatch data for
  // the workbench rows + sidebar Validation Issues) — see design Section 6.2.
  const { extractions } = useDocumentExtractions(jobId, docs, requestContext);

  if (loading) {
    return (
      <div className="flex min-h-[20vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {mode !== 'portal' && (
        <ReminderLogDialog
          open={reminderOpen}
          onOpenChange={setReminderOpen}
          logs={reminderLogs}
          loading={reminderLoading}
          error={reminderErr}
          claimNumber={claimNumber}
        />
      )}

      {/* Summary bar */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-5 py-2.5">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            <strong className="text-gray-900">{uploadedCount}</strong>
            {' '}of{' '}
            <strong className="text-gray-900">{totalDocs}</strong>
            {' '}documents uploaded
          </span>
          {/* Progress bar */}
          <div className="hidden sm:block w-32 h-1.5 bg-gray-200 rounded-full overflow-hidden ml-2">
            <div
              className="h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${Math.min(100, (uploadedCount / totalDocs) * 100)}%` }}
            />
          </div>
        </div>
        {loadErr && (
          <button onClick={loadDocs} className="text-xs text-red-500 hover:underline">
            {loadErr} — Retry
          </button>
        )}
        {mode !== 'portal' && (
          <button
            onClick={openReminderLogs}
            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Logs
          </button>
        )}
        <span className="text-xs text-gray-400">Stored in MinIO S3</span>
      </div>

      {/* Workbench (table/row layout) + sidebar, side by side on desktop,
          stacked below the content on smaller screens — normal page
          scroll, no fixed-height/internal-scroll wrapper (see design
          Section 6.7; DocumentsWorkbench itself still handles its own
          `md` row-layout breakpoint via useBreakpoint). */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start" data-testid="documents-desktop-layout">
        <div className="min-w-0 flex-1">
          <DocumentsWorkbench
            documentGroups={DOCUMENT_GROUPS}
            docs={docs}
            extractions={extractions}
            jobId={jobId}
            onUploaded={onUploaded}
            onDeleted={onDeleted}
            onView={onView}
            mode={mode}
            context={requestContext}
            readOnly={isClaimOfficeViewOnly}
            deleteDisabled={deleteDisabled}
          />
        </div>
        <div className="w-full lg:w-80 lg:flex-shrink-0">
          <DocumentsSidebar documentGroups={DOCUMENT_GROUPS} docs={docs} extractions={extractions} />
        </div>
      </div>

      <p className="text-xs text-gray-400 text-center pb-2">
        Supported: PDF, JPG, PNG, HEIC, DOC, DOCX · Max 20 MB per file
      </p>

      {/* Side-by-side OCR viewer — for all non-invoice/estimate docs */}
      {viewerDoc && (
        <DocOCRModal
          doc={viewerDoc}
          docLabel={allDefs.find(d => d.key === viewerDoc.doc_type)?.label ?? viewerDoc.doc_type}
          jobId={jobId}
          onClose={() => setViewerDoc(null)}
        />
      )}

      {/* Extraction modal — for invoices and estimates */}
      {extractionDoc && (
        <ClaimExtractionModal
          doc={extractionDoc}
          docLabel={allDefs.find(d => d.key === extractionDoc.doc_type)?.label ?? extractionDoc.doc_type}
          jobId={jobId}
          onClose={() => setExtractionDoc(null)}
          siblingDocs={siblingDocs}
        />
      )}
    </div>
  );
}
